import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const inputVariants = cva(
  "flex w-full rounded-lg border bg-background text-base ring-offset-background transition-all duration-200 file:border-0 file:bg-transparent file:text-base file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "border-input focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        glass:
          "border-border/50 bg-card/50 backdrop-blur-sm focus-visible:border-primary/50 focus-visible:bg-card/80",
        trading:
          "border-border bg-background-secondary font-mono tabular-nums focus-visible:border-primary focus-visible:shadow-neon",
        search:
          "border-transparent bg-secondary focus-visible:bg-secondary/80 focus-visible:ring-2 focus-visible:ring-ring",
      },
      inputSize: {
        default: "h-10 px-3 py-2",
        sm: "h-9 px-3 py-1 text-base",
        lg: "h-12 px-4 py-3 text-base",
        xl: "h-14 px-5 py-4 text-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      inputSize: "default",
    },
  }
);

export interface InputProps
  extends Omit<React.ComponentProps<"input">, "size">,
    VariantProps<typeof inputVariants> {
  /** Icon to display at the start of the input */
  startIcon?: React.ReactNode;
  /** Icon to display at the end of the input */
  endIcon?: React.ReactNode;
  /** Error state */
  error?: boolean;
  /** Success state */
  success?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      variant,
      inputSize,
      startIcon,
      endIcon,
      error,
      success,
      ...props
    },
    ref
  ) => {
    // If we have icons, wrap in a container
    if (startIcon || endIcon) {
      return (
        <div className="relative flex items-center">
          {startIcon && (
            <div className="absolute left-3 flex items-center pointer-events-none text-muted-foreground">
              {startIcon}
            </div>
          )}
          <input
            type={type}
            className={cn(
              inputVariants({ variant, inputSize }),
              startIcon && "pl-10",
              endIcon && "pr-10",
              error && "border-destructive focus-visible:ring-destructive/50",
              success && "border-success focus-visible:ring-success/50",
              className
            )}
            ref={ref}
            {...props}
          />
          {endIcon && (
            <div className="absolute right-3 flex items-center pointer-events-none text-muted-foreground">
              {endIcon}
            </div>
          )}
        </div>
      );
    }

    return (
      <input
        type={type}
        className={cn(
          inputVariants({ variant, inputSize }),
          error && "border-destructive focus-visible:ring-destructive/50",
          success && "border-success focus-visible:ring-success/50",
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);
Input.displayName = "Input";

// Specialized input for trading amounts
interface TradingInputProps extends Omit<InputProps, "variant" | "type"> {
  currency?: string;
  onMaxClick?: () => void;
}

const TradingInput = React.forwardRef<HTMLInputElement, TradingInputProps>(
  ({ currency = "SOL", onMaxClick, className, ...props }, ref) => {
    return (
      <div className="relative flex items-center">
        <input
          type="number"
          inputMode="decimal"
          step="any"
          min="0"
          className={cn(
            inputVariants({ variant: "trading", inputSize: "lg" }),
            "pr-20",
            className
          )}
          ref={ref}
          {...props}
        />
        <div className="absolute right-3 flex items-center gap-2">
          {onMaxClick && (
            <button
              type="button"
              onClick={onMaxClick}
              className="text-xs font-semibold text-primary hover:text-primary/80 transition-colors"
            >
              MAX
            </button>
          )}
          <span className="text-sm font-medium text-muted-foreground">
            {currency}
          </span>
        </div>
      </div>
    );
  }
);
TradingInput.displayName = "TradingInput";

export { Input, TradingInput, inputVariants };
